import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Store, Mail, Lock, AlertCircle } from 'lucide-react';

export default function VendorLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(credentials.email, credentials.password);
      
      // Ensure user is a vendor
      if (user.role !== 'vendor') {
        setError('This login is for vendors only. Please use the admin login.');
        setLoading(false);
        return;
      }

      toast.success('Welcome back!');
      navigate('/vendor');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = async (email, password, label) => {
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      
      if (user.role !== 'vendor') {
        setError('This login is for vendors only.');
        setLoading(false);
        return;
      }

      toast.success(`Logged in as ${label}`);
      navigate('/vendor');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/r2yn-logo-color.png" 
            alt="R2YN - Rushing 2 Your Needs" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold" data-font="display">Vendor Portal</h1>
          <p className="text-muted-foreground text-sm mt-2">
            Manage your business on R2YN
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5 text-primary" />
              Vendor Sign In
            </CardTitle>
            <CardDescription>
              Enter your credentials to access your vendor dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="vendor@restaurant.com"
                    value={credentials.email}
                    onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="vendor-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="vendor-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="vendor-login-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Quick Test Login</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleQuickLogin('freshmarketplus@grocery.local', 'Vendor123!', 'Grocery Store')}
                  disabled={loading}
                  data-testid="quick-login-grocery"
                  className="text-xs"
                >
                  🛒 Grocery
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleQuickLogin('homecleanpro@service.local', 'Vendor123!', 'Service Provider')}
                  disabled={loading}
                  data-testid="quick-login-service"
                  className="text-xs"
                >
                  🔧 Service
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleQuickLogin('demovendor@r2yn.com', 'Demo123456', 'Restaurant')}
                  disabled={loading}
                  data-testid="quick-login-restaurant"
                  className="text-xs"
                >
                  🍕 Restaurant
                </Button>
              </div>
              
              <p className="text-xs text-center text-muted-foreground mt-2">
                Click any button above for instant login with test data
              </p>
            </form>

            <div className="mt-6 text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                New vendor?{' '}
                <Button 
                  variant="link" 
                  className="p-0 h-auto font-medium"
                  onClick={() => navigate('/vendor/signup')}
                >
                  Create an Account
                </Button>
              </p>
              <p className="text-sm text-muted-foreground">
                Are you an administrator?{' '}
                <Button 
                  variant="link" 
                  className="p-0 h-auto font-medium"
                  onClick={() => navigate('/login')}
                >
                  Admin Login
                </Button>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          R2YN Platform v2.0 — Rushing 2 Your Needs — Secure vendor access
        </p>
      </div>
    </div>
  );
}
